import { useEffect, useState, useCallback } from "react";
import { useIsPlayerOnly } from "../hooks/useIsPlayerOnly";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Circle,
  Group,
  Text,
} from "react-konva";
import useImage from "use-image";
import {
  Trash2,
  ArrowLeft,
  Pencil,
  Globe,
  Share2,
  PencilRuler,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  Users,
  Link as LinkIcon,
  Unlink,
} from "lucide-react";

import DefenderGlyph from "../components/DefenderGlyph";
import PlayArrow from "../components/PlayArrow";
import { useTranslation as useTranslationOrig } from "react-i18next";
import i18n from "../i18n";
console.log("i18n.language:", i18n.language);
console.log("FR namespaces:", Object.keys(i18n.getDataByLanguage("fr") || {}));
console.log("Has playDetails?", i18n.hasResourceBundle("fr", "playDetails"));

// ---- i18n safe hook (évite TS2589) + helper qui retourne TOUJOURS une string
type RawT = (key: string, options?: Record<string, unknown>) => any;
type UseTranslationNS = (ns?: string | string[]) => { t: RawT };
const useTranslationSafe = useTranslationOrig as unknown as UseTranslationNS;

type Tag = { id: string; name: string };
type Pt = { x: number; y: number };
type Team = { id: string; team_name: string };

export const PlayDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // i18n
 const { t: tRaw } = useTranslationSafe(["common", "playDetails"]);

const tr = useCallback((key: string, options?: Record<string, unknown>): string => {
  // convertit "playDetails.xxx" -> "playDetails:xxx"
  const fixedKey = key.startsWith("playDetails.")
    ? `playDetails:${key.slice("playDetails.".length)}`
    : key;

  const out = tRaw(fixedKey as any, options as any);
  if (typeof out === "string") return out;
  if (Array.isArray(out)) return out.join("");
  return String(out ?? "");
}, [tRaw]);

  const [play, setPlay] = useState<any>(null);
  const [image] = useImage("/court.png");

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showEditName, setShowEditName] = useState(false);
  const [newName, setNewName] = useState("");

  const [stageWidth, setStageWidth] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);

  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { isPlayerOnly } = useIsPlayerOnly();
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const [currentPhase, setCurrentPhase] = useState(0);
  const [showAllPhases, setShowAllPhases] = useState(false);

  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [copied, setCopied] = useState(false);

  const [authorName, setAuthorName] = useState<string>("");

  // équipes
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamById, setTeamById] = useState<Record<string, Team>>({});
  const [showAssignTeam, setShowAssignTeam] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  // ---------------- user, tags, play ----------------
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("play_tags").select("*");
      setAvailableTags(data || []);
    })();
  }, []);

  // Charger les équipes de l’utilisateur
  useEffect(() => {
    if (!currentUserId) return;
    (async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name")
        .eq("user_id", currentUserId)
        .or("archived.is.null,archived.eq.false");
      if (!error && data) {
        setTeams(data);
        setTeamById(Object.fromEntries(data.map(t => [t.id, t])));
      }
    })();
  }, [currentUserId]);

  useEffect(() => {
    const fetchPlay = async () => {
      const { data, error } = await supabase
        .from("plays")
        .select("*, user_profiles ( full_name, pseudo, public_use_pseudo )")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Erreur récupération play", error);
        return;
      }

      setPlay(data);
      setDescription(data?.description || "");
      setVideoUrl(data?.video_url || "");

      let name =
        data?.user_profiles?.public_use_pseudo && data?.user_profiles?.pseudo
          ? data.user_profiles.pseudo
          : data?.user_profiles?.full_name || "";

      if (!name && data?.user_id) {
        try {
          const { data: pub } = await supabase.rpc("get_public_profile", { uid: data.user_id });
          const row = Array.isArray(pub) ? pub[0] : pub;
          if (row?.display_name) name = row.display_name;
        } catch { /* noop */ }
      }
      setAuthorName(name || tr("playDetails.fallback.user", { defaultValue: "Utilisateur" }));

      // Si une team est déjà liée mais pas dans la liste (play partagé d’un autre user), on la récupère pour l’affichage
      if (data?.team_id && !teamById[data.team_id]) {
        const { data: t } = await supabase.from("teams").select("id, team_name").eq("id", data.team_id).maybeSingle();
        if (t) {
          setTeamById(prev => ({ ...prev, [t.id]: t }));
        }
      }
    };

    if (id) fetchPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------------- stage size ----------------
  useEffect(() => {
    if (!image) return;
    const handleResize = () => {
      const naturalW = image.naturalWidth;
      const naturalH = image.naturalHeight;
      const availW = Math.min(window.innerWidth * 0.9, 750);
      const scale = availW / naturalW;
      setStageWidth(availW);
      setStageHeight(naturalH * scale);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [image]);

  // ---------------- mapping coordonnées ----------------
  const phaseIsNormalized = (phase: any) => {
    let maxX = 0, maxY = 0;
    (phase?.players ?? []).forEach((p: any) => { if (!p) return; maxX = Math.max(maxX, +p.x || 0); maxY = Math.max(maxY, +p.y || 0); });
    (phase?.defenders ?? []).forEach((d: any) => { if (!d) return; maxX = Math.max(maxX, +d.x || 0); maxY = Math.max(maxY, +d.y || 0); });
    (phase?.arrows ?? []).forEach((a: any) => {
      const pts = [a?.from, a?.to, a?.control].filter(Boolean);
      pts.forEach((v: any) => { maxX = Math.max(maxX, +v.x || 0); maxY = Math.max(maxY, +v.y || 0); });
    });
    return maxX <= 1.01 && maxY <= 1.01;
  };

  const getPixelBase = () => {
    const m = play?.meta || play?.data_meta || {};
    const bw = m.stageW ?? m.baseW ?? m.w ?? play?.canvasSize?.w;
    const bh = m.stageH ?? m.baseH ?? m.h ?? play?.canvasSize?.h;
    if (bw && bh) return { bw: Number(bw), bh: Number(bh) };
    const natW = image?.naturalWidth || stageWidth || 1;
    const natH = image?.naturalHeight || stageHeight || 1;
    return { bw: natW, bh: natH };
  };

  const toCanvasPt = (phase: any, pt: Pt): Pt => {
    if (!pt) return pt;
    if (phaseIsNormalized(phase)) {
      return { x: pt.x * stageWidth, y: pt.y * stageHeight };
    }
    const { bw, bh } = getPixelBase();
    return { x: (pt.x / bw) * stageWidth, y: (pt.y / bh) * stageHeight };
  };

  // ---------------- nav clavier ----------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (showAllPhases || !play?.data?.length) return;
      if (e.key === "ArrowLeft") setCurrentPhase((p) => Math.max(p - 1, 0));
      else if (e.key === "ArrowRight") setCurrentPhase((p) => Math.min(p + 1, play.data.length - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAllPhases, play?.data?.length]);

  if (!play || !play.data || !image) return <p>{tr("common.loading", { defaultValue: "Chargement..." })}</p>;

  const assignedTeam = play.team_id ? teamById[play.team_id] : null;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => navigate("/allplays")}
          className="p-2 hover:bg-gray-200 rounded"
          title={tr("playDetails.actions.backToPlays", { defaultValue: "Retour aux plays" })}
        >
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>

        {currentUserId === play.user_id && (
          <>
            <button
              onClick={() => {
                setNewName(play.name);
                setSelectedTagIds(play.tag_ids || []);
                setShowEditName(true);
              }}
              className="p-2 hover:bg-gray-200 rounded"
              title={tr("playDetails.actions.rename", { defaultValue: "Renommer le play" })}
            >
              <Pencil className="w-5 h-5 text-gray-700" />
            </button>

            {!isPlayerOnly && (
              <button
                onClick={() => navigate(`/editplay/${play.id}`)}
                className="p-2 hover:bg-yellow-100 rounded"
                title={tr("playDetails.actions.editInEditor", { defaultValue: "Modifier dans l'éditeur" })}
              >
                <PencilRuler className="w-6 h-6 text-yellow-600" />
              </button>
            )}

            {/* Assigner à une équipe */}
            <button
              onClick={() => {
                setSelectedTeamId(play.team_id ?? null);
                setShowAssignTeam(true);
              }}
              className="flex items-center gap-1 px-3 py-2 rounded hover:bg-blue-100 transition"
              title={
                play.team_id
                  ? tr("playDetails.actions.changeTeam", { defaultValue: "Changer d'équipe" })
                  : tr("playDetails.actions.assignTeam", { defaultValue: "Assigner à une équipe" })
              }
            >
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">
                {play.team_id
                  ? tr("playDetails.actions.changeTeam", { defaultValue: "Changer d’équipe" })
                  : tr("playDetails.actions.assignTeam", { defaultValue: "Assigner à une équipe" })}
              </span>
            </button>

            {play.is_public ? (
              <button
                onClick={async () => {
                  const { error } = await supabase
                    .from("plays")
                    .update({ is_public: false, description: null, video_url: null })
                    .eq("id", play.id);
                  if (!error) {
                    setPlay((prev: any) => ({
                      ...prev,
                      is_public: false,
                      description: null,
                      video_url: null,
                    }));
                  }
                }}
                className="flex items-center gap-1 px-3 py-2 rounded hover:bg-red-100 transition"
                title={tr("playDetails.actions.unpublish", { defaultValue: "Annuler le partage public" })}
              >
                <Globe className="w-5 h-5 text-green-500" />
                <span className="text-sm text-red-600 font-medium">
                  {tr("playDetails.actions.unpublishShort", { defaultValue: "Annuler partage" })}
                </span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setDescription(play.description || "");
                  setVideoUrl(play.video_url || "");
                  setShowPublishPopup(true);
                }}
                className="flex items-center gap-1 px-3 py-2 rounded hover:bg-green-100 transition"
                title={tr("playDetails.actions.publish", { defaultValue: "Partager publiquement" })}
              >
                <Globe className="w-5 h-5 text-gray-500 opacity-60" />
                <span className="text-sm text-gray-600 line-through font-medium">
                  {tr("playDetails.badges.notPublished", { defaultValue: "Non partagé" })}
                </span>
              </button>
            )}

            {play.is_shared ? (
              <button
                onClick={async () => {
                  const { error } = await supabase
                    .from("plays")
                    .update({ is_shared: false })
                    .eq("id", play.id);
                  if (!error) setPlay((prev: any) => ({ ...prev, is_shared: false }));
                }}
                className="flex items-center gap-1 px-3 py-2 rounded hover:bg-red-100 transition"
                title={tr("playDetails.actions.unshareLink", { defaultValue: "Annuler le partage par lien" })}
              >
                <Share2 className="w-5 h-5 text-green-600" />
                <span className="text-sm text-red-600 font-medium">
                  {tr("playDetails.actions.unshareLinkShort", { defaultValue: "Annuler partage lien" })}
                </span>
              </button>
            ) : (
              <button
                onClick={() => setShowLinkPopup(true)}
                className="flex items-center gap-1 px-3 py-2 rounded hover:bg-green-100 transition"
                title={tr("playDetails.actions.shareLink", { defaultValue: "Partager par lien uniquement" })}
              >
                <Share2 className="w-5 h-5 text-gray-500 opacity-60" />
                <span className="text-sm text-gray-600 line-through font-medium">
                  {tr("playDetails.badges.notShared", { defaultValue: "Non partagé" })}
                </span>
              </button>
            )}

            <button
              onClick={() => setShowConfirmDelete(true)}
              className="p-2 hover:bg-red-100 rounded"
              title={tr("playDetails.actions.delete", { defaultValue: "Supprimer le play" })}
            >
              <Trash2 className="w-6 h-6 text-red-600" />
            </button>
          </>
        )}
      </div>

      <h1 className="text-2xl font-bold mb-2">{play.name}</h1>

      {/* Badge équipe assignée */}
      <div className="mb-4">
        {assignedTeam ? (
          <button
            onClick={() => navigate(`/team/${assignedTeam.id}`)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 text-violet-800 text-sm"
            title={tr("playDetails.actions.viewTeam", { defaultValue: "Voir l’équipe" })}
          >
            <LinkIcon className="w-4 h-4" />
            {tr("playDetails.assignedTeam", {
              defaultValue: "Équipe assignée : ",
            })}
            <span className="font-semibold">{assignedTeam.team_name}</span>
          </button>
        ) : (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm">
            <Unlink className="w-4 h-4" />
            {tr("playDetails.noTeam", { defaultValue: "Aucune équipe assignée" })}
          </span>
        )}
      </div>

      {play.user_id && (
        <p className="text-sm text-gray-600 mb-2">
          {tr("playDetails.author", { defaultValue: "Auteur : " })}
          <span
            onClick={() => navigate(`/profile/${play.user_id}`)}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            {authorName || tr("playDetails.fallback.user", { defaultValue: "Utilisateur" })}
          </span>
        </p>
      )}

      {play.is_public && (
        <div className="mb-4">
          {play.description && (
            <p className="text-gray-700 text-sm mb-2 whitespace-pre-wrap">
              {play.description}
            </p>
          )}
          {play.video_url && (
            <a
              href={play.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 text-sm underline"
            >
              {tr("playDetails.viewVideo", { defaultValue: "Voir la vidéo du play" })}
            </a>
          )}
        </div>
      )}

      {play.tag_ids && play.tag_ids.length > 0 && availableTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {availableTags
            .filter((tag) => play.tag_ids.includes(tag.id))
            .map((tag) => (
              <span
                key={tag.id}
                className="bg-blue-100 text-blue-800 px-3 py-1 text-sm rounded-full"
              >
                {tag.name}
              </span>
            ))}
        </div>
      )}

      {!showAllPhases && (
        <div className="flex justify-between items-center mb-2">
          <button
            disabled={currentPhase === 0}
            onClick={() => setCurrentPhase((p) => Math.max(p - 1, 0))}
            className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">
              {tr("playDetails.prev", { defaultValue: "Précédente" })}
            </span>
          </button>

          <span className="text-sm text-gray-700">
            {tr("playDetails.phaseCounter", {
              defaultValue: "Phase {{current}} / {{total}}",
              current: currentPhase + 1,
              total: play.data.length,
            })}
          </span>

          <button
            disabled={currentPhase === play.data.length - 1}
            onClick={() => setCurrentPhase((p) => Math.min(p + 1, play.data.length - 1))}
            className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
          >
            <span className="text-sm">
              {tr("playDetails.next", { defaultValue: "Suivante" })}
            </span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex justify-center mb-4">
        <button
          onClick={() => setShowAllPhases((p) => !p)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded flex items-center gap-2"
        >
          <ArrowRightLeft className="w-4 h-4" />
          <span className="text-sm font-medium">
            {showAllPhases
              ? tr("playDetails.viewStepByStep", { defaultValue: "Phase par phase" })
              : tr("playDetails.viewAllPhases", { defaultValue: "Voir toutes les phases" })}
          </span>
        </button>
      </div>

      {(showAllPhases ? play.data : [play.data[currentPhase]]).map(
        (phase: any, phaseIndex: number) => (
          <div key={phaseIndex} className="flex justify-center mb-8">
            <Stage width={stageWidth} height={stageHeight}>
              <Layer>
                <KonvaImage image={image} width={stageWidth} height={stageHeight} />

                {/* Défenseurs */}
                {(phase.defenders ?? []).map((d: any, i: number) => {
                  const D = toCanvasPt(phase, d);
                  return <DefenderGlyph key={`def-${i}`} x={D.x} y={D.y} angle={d.angle ?? 0} />;
                })}

                {(phase.arrows ?? []).map((arrow: any, i: number) => {
                  const fromRaw =
                    arrow.fromPlayerIndex !== undefined
                      ? phase.players[arrow.fromPlayerIndex]
                      : arrow.from;
                  const toRaw =
                    arrow.toPlayerIndex !== undefined
                      ? phase.players[arrow.toPlayerIndex]
                      : arrow.to;

                  if (!fromRaw || !toRaw) return null;

                  return (
                    <PlayArrow
                      key={`arrow-${i}`}
                      type={arrow.type}
                      from={toCanvasPt(phase, fromRaw)}
                      to={toCanvasPt(phase, toRaw)}
                      control={arrow.control ? toCanvasPt(phase, arrow.control) : null}
                      fromIsPlayer={arrow.fromPlayerIndex !== undefined}
                      toIsPlayer={arrow.toPlayerIndex !== undefined}
                    />
                  );
                })}

                {/* JOUEURS */}
                {phase.players.map((p: any, i: number) => {
                  const P = toCanvasPt(phase, p);
                  return (
                    <Group key={i} x={P.x} y={P.y}>
                      {p.hasBall ? (
                        <>
                          <Circle radius={15} stroke="black" strokeWidth={2} />
                          <Text
                            text={p.number.toString()}
                            fontSize={16}
                            fill="black"
                            align="center"
                            verticalAlign="middle"
                            width={30}
                            height={30}
                            offsetX={15}
                            offsetY={15}
                          />
                        </>
                      ) : (
                        <Text
                          text={p.number.toString()}
                          fontSize={16}
                          fill="black"
                          align="center"
                          verticalAlign="middle"
                          width={30}
                          height={30}
                          offsetX={15}
                          offsetY={15}
                        />
                      )}
                    </Group>
                  );
                })}
              </Layer>
            </Stage>
          </div>
        )
      )}

      {/* Popup partage par lien */}
      {showLinkPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-lg">
            <h2 className="text-lg font-bold mb-4">
              {tr("playDetails.link.title", { defaultValue: "Lien de partage" })}
            </h2>

            {!play.is_shared ? (
              <>
                <p className="text-sm text-gray-700 mb-4">
                  {tr("playDetails.link.notSharedYet", {
                    defaultValue: "Ce play n’est pas encore partagé par lien. Cliquez ci-dessous pour l’activer.",
                  })}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    className="px-4 py-2 bg-gray-300 rounded"
                    onClick={() => setShowLinkPopup(false)}
                  >
                    {tr("common.cancel", { defaultValue: "Annuler" })}
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={async () => {
                      const { error } = await supabase
                        .from("plays")
                        .update({ is_shared: true })
                        .eq("id", play.id);
                      if (!error) {
                        setPlay((prev: any) => ({ ...prev, is_shared: true }));
                      }
                    }}
                  >
                    {tr("playDetails.link.enable", { defaultValue: "Activer le partage" })}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  {tr("playDetails.link.copyHelp", { defaultValue: "Voici le lien pour partager ce play :" })}
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/sharedplay/${play.id}`}
                    className="w-full border px-3 py-2 rounded text-sm text-gray-800"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/sharedplay/${play.id}`);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    {tr("common.copy", { defaultValue: "Copier" })}
                  </button>
                  {copied && (
                    <span className="text-green-600 text-sm font-medium">
                      {tr("common.copied", { defaultValue: "Lien copié ✅" })}
                    </span>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowLinkPopup(false)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                  >
                    {tr("common.close", { defaultValue: "Fermer" })}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modale suppression */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-sm">
            <h2 className="text-lg font-bold mb-4">
              {tr("playDetails.delete.title", { defaultValue: "Supprimer ce play ?" })}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {tr("playDetails.delete.subtitle", { defaultValue: "Cette action est irréversible." })}
            </p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setShowConfirmDelete(false)}>
                {tr("common.cancel", { defaultValue: "Annuler" })}
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={async () => {
                  const { error } = await supabase.from("plays").delete().eq("id", play.id);
                  if (error) {
                    alert(tr("playDetails.delete.error", { defaultValue: "Erreur lors de la suppression." }));
                    console.error(error);
                  } else {
                    navigate("/allplays");
                  }
                }}
              >
                {tr("common.delete", { defaultValue: "Supprimer" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale renommer + tags */}
      {showEditName && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-sm">
            <h2 className="text-lg font-bold mb-4">
              {tr("playDetails.edit.title", { defaultValue: "Modifier le play" })}
            </h2>

            <label className="block text-sm font-medium mb-1">
              {tr("playDetails.edit.nameLabel", { defaultValue: "Nom" })}
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
            />

            <label className="block text-sm font-medium mb-1">
              {tr("playDetails.edit.tagsLabel", { defaultValue: "Tags" })}
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {availableTags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() =>
                      setSelectedTagIds((prev) =>
                        isSelected ? prev.filter((tid) => tid !== tag.id) : [...prev, tag.id]
                      )
                    }
                    className={`px-3 py-1 text-sm rounded-full border ${
                      isSelected
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-100 text-gray-800 border-gray-300"
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setShowEditName(false)}>
                {tr("common.cancel", { defaultValue: "Annuler" })}
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={async () => {
                  const { error } = await supabase
                    .from("plays")
                    .update({ name: newName, tag_ids: selectedTagIds })
                    .eq("id", play.id);

                  if (error) {
                    alert(tr("playDetails.edit.error", { defaultValue: "Erreur lors de la mise à jour." }));
                    console.error(error);
                  } else {
                    setPlay((prev: any) => ({ ...prev, name: newName, tag_ids: selectedTagIds }));
                    setShowEditName(false);
                  }
                }}
              >
                {tr("common.save", { defaultValue: "Enregistrer" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale partage public */}
      {showPublishPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-lg">
            <h2 className="text-lg font-bold mb-4">
              {tr("playDetails.publish.title", { defaultValue: "Partager ce play" })}
            </h2>

            <label className="block text-sm font-medium mb-1">
              {tr("playDetails.publish.descriptionLabel", { defaultValue: "Description (optionnel)" })}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
              rows={3}
              placeholder={tr("playDetails.publish.descriptionPlaceholder", {
                defaultValue: "Décris le contexte ou le but du play",
              })}
            />

            <label className="block text-sm font-medium mb-1">
              {tr("playDetails.publish.videoLabel", { defaultValue: "Lien vers une vidéo (optionnel)" })}
            </label>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
              placeholder={tr("playDetails.publish.videoPlaceholder", {
                defaultValue: "https://youtube.com/...",
              })}
            />

            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setShowPublishPopup(false)}>
                {tr("common.cancel", { defaultValue: "Annuler" })}
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={async () => {
                  const { error } = await supabase
                    .from("plays")
                    .update({ is_public: true, description, video_url: videoUrl })
                    .eq("id", play.id);

                  if (error) {
                    alert(tr("playDetails.publish.error", { defaultValue: "Erreur lors du partage." }));
                    console.error(error);
                  } else {
                    setPlay((prev: any) => ({
                      ...prev,
                      is_public: true,
                      description,
                      video_url: videoUrl,
                    }));
                    setShowPublishPopup(false);
                  }
                }}
              >
                {tr("playDetails.publish.confirm", { defaultValue: "Partager" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale assignation équipe */}
      {showAssignTeam && currentUserId === play.user_id && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">
              {tr("playDetails.assign.title", { defaultValue: "Assigner le play à une équipe" })}
            </h2>

            <label className="block text-sm font-medium mb-1">
              {tr("playDetails.assign.teamLabel", { defaultValue: "Équipe" })}
            </label>
            <select
              className="w-full border px-3 py-2 rounded mb-4"
              value={selectedTeamId ?? ""}
              onChange={(e) => setSelectedTeamId(e.target.value || null)}
            >
              <option value="">
                {tr("playDetails.assign.noneOption", { defaultValue: "— Aucune —" })}
              </option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.team_name}</option>
              ))}
            </select>

            <div className="flex justify-between items-center">
              {play.team_id && (
                <button
                  onClick={async () => {
                    const { error } = await supabase.from("plays").update({ team_id: null }).eq("id", play.id);
                    if (!error) {
                      setPlay((prev: any) => ({ ...prev, team_id: null }));
                      setSelectedTeamId(null);
                      setShowAssignTeam(false);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-50 text-red-700 hover:bg-red-100"
                >
                  <Unlink className="w-4 h-4" />
                  {tr("playDetails.assign.removeTeam", { defaultValue: "Retirer l’équipe" })}
                </button>
              )}
              <div className="ml-auto flex gap-2">
                <button className="px-4 py-2 bg-gray-200 rounded" onClick={() => setShowAssignTeam(false)}>
                  {tr("common.cancel", { defaultValue: "Annuler" })}
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={async () => {
                    const payload = selectedTeamId ? { team_id: selectedTeamId } : { team_id: null };
                    const { error } = await supabase.from("plays").update(payload).eq("id", play.id);
                    if (error) {
                      alert(tr("playDetails.assign.error", { defaultValue: "Erreur lors de l’assignation." }));
                      console.error(error);
                      return;
                    }
                    setPlay((prev: any) => ({ ...prev, ...payload }));
                    setShowAssignTeam(false);
                  }}
                >
                  {tr("common.save", { defaultValue: "Enregistrer" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
